import { Box, InputBase, Link, Stack, Typography } from "@mui/material";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import MfaSetupInstructions from "components/Auth/MfaSetupInstructions";
import { AcxMessageBox as MessageBox } from "components/UI/AcxMessageBox";
import AcxButton from "components/UI/AcxButton";
import AcxMainTextField from "components/UI/AcxMainTextField";
import AcxLogoLoader from "components/UI/Loaders/AcxLogoLoader";
import localForage from "localforage";
import theme from "Theme/AppTheme";
import React, { useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { AuthStore } from "stores/AuthStore";
import useCooldown from "utils/useCooldown";
import { useStore } from "utils/useStore";
import authService, {
    AuthenticationResultStatus,
} from "./api-authorization/AuthorizeService";
import { SamlLoginProvider } from "./LoginUser";

const emptyUserError = "Please enter your username.";
const emptyPasswordError = "Please enter your password.";
const emptyError = "Please enter your username and password.";
const accountLockedMessage = "Account locked";
const defaultLocalStoreDescription = "default browser data store";
const defaultLocalStoreName = "AcxDefault";
const jsonContentType = "application/json";
const loginFailErrorCode = "LoginFail";
const loginOrganizationIdHeaderName = "Acx-Login-OrganizationId";
const pendingMfaTargetOrgIdStorageKey = "pendingMfaTargetOrgId";
const requiresMfaSetupStatus = "RequiresMfaSetup";
const requiresMfaStatus = "RequiresMfa";
const selectedOrganizationStorageKey = "selectedOrgID";
const authenticatorAppMethod = "authenticatorApp";
const emailMethod = "email";
const mfaNarrowFormWidth = 310;
const mfaWideFormWidth = 430;
const emailCodeResendCooldownSeconds = 60;

type MfaMethod = typeof authenticatorAppMethod | typeof emailMethod;
type LoginStep =
    | "login"
    | "verify"
    | "setupMethod"
    | "setup"
    | "emailSetup"
    | "setupComplete";

type LoginResponse =
    | {
          status: typeof requiresMfaStatus;
          availableMethods?: MfaMethod[];
          setupMethods?: MfaMethod[];
          challengeId?: string;
          organizationId?: string;
      }
    | {
          status: typeof requiresMfaSetupStatus;
          challengeId: string;
          availableMethods?: MfaMethod[];
      }
    | { status?: undefined };

type MfaSetupResponse = {
    manualEntryKey: string;
    authenticatorUri: string;
};

type MfaSetupCompleteResponse = {
    recoveryCodes?: string[];
};

type MfaEmailResponse = {
    maskedEmail: string;
};

export default function Login({
    samlLoginProvider,
}: {
    samlLoginProvider?: SamlLoginProvider | null;
}) {
    const [username, setUsername] = useState("");
    const [password, setPassword] = useState("");
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [loginSuccess, setLoginSuccess] = useState(false);
    const [isAccountLocked, setIsAccountLocked] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string>("");
    const [step, setStep] = useState<LoginStep>("login");
    const [challengeId, setChallengeId] = useState<string>();
    const [mfaCode, setMfaCode] = useState("");
    const [manualEntryKey, setManualEntryKey] = useState("");
    const [authenticatorUri, setAuthenticatorUri] = useState("");
    const [recoveryCodes, setRecoveryCodes] = useState<string[]>([]);
    const [useRecoveryCode, setUseRecoveryCode] = useState(false);
    const [availableMfaMethods, setAvailableMfaMethods] = useState<MfaMethod[]>(
        [authenticatorAppMethod],
    );
    const [verificationMfaMethods, setVerificationMfaMethods] = useState<
        MfaMethod[]
    >([authenticatorAppMethod]);
    const [setupMfaMethods, setSetupMfaMethods] = useState<MfaMethod[]>([]);
    const [selectedMfaMethod, setSelectedMfaMethod] = useState<MfaMethod>(
        authenticatorAppMethod,
    );
    const [emailDestination, setEmailDestination] = useState("");
    const [emailCodeResent, setEmailCodeResent] = useState(false);
    const [requestedLoginOrganizationId, setRequestedLoginOrganizationId] =
        useState<string>();
    const emailCodeInputRefs = useRef<Array<HTMLInputElement | null>>([]);
    const {
        isCoolingDown: isEmailCodeCoolingDown,
        remainingSeconds: emailCodeCooldownSecondsRemaining,
        reset: resetEmailCodeCooldown,
        start: startEmailCodeCooldown,
    } = useCooldown(emailCodeResendCooldownSeconds);

    const navigate = useNavigate();
    const authStore = useStore(AuthStore);

    const [searchParams] = useSearchParams();
    const returnURL = searchParams.get("ReturnUrl");

    const getSamlUrl = (providerKey?: string) => {
        const params = new URLSearchParams();
        if (returnURL && returnURL.startsWith("/")) {
            params.set("returnUrl", returnURL);
        }
        if (providerKey) {
            params.set("providerKey", providerKey);
        }

        const queryString = params.toString();
        return `/api/Saml/InitiateSingleSignOn${
            queryString ? `?${queryString}` : ""
        }`;
    };

    const getValidatedReturnUrl = () => {
        const resolved = new URL(returnURL || "/app", window.location.origin);
        return resolved.origin === window.location.origin
            ? resolved
            : new URL("/app", window.location.origin);
    };

    const persistLoginOrganization = async (organizationId?: string) => {
        if (!organizationId) {
            return;
        }

        sessionStorage.setItem(pendingMfaTargetOrgIdStorageKey, organizationId);

        const localStore = localForage.createInstance({
            name: defaultLocalStoreName,
            description: defaultLocalStoreDescription,
        });

        await localStore.setItem(
            selectedOrganizationStorageKey,
            organizationId,
        );
    };

    const redirectToReturnUrl = async (organizationId?: string) => {
        await persistLoginOrganization(organizationId);
        const target = getValidatedReturnUrl();

        // An in-flight IdentityServer transaction
        if (target.pathname.startsWith("/connect/")) {
            window.location.replace(target.toString());
            return;
        }

        // The login POST established the IdentityServer session, so tokens
        // can be minted silently (hidden iframe)
        const result = await authService.signIn();
        if (result.status !== AuthenticationResultStatus.Success) {
            window.location.replace(target.toString());
            return;
        }

        await authStore.populateAuth();
        navigate(`${target.pathname}${target.search}${target.hash}`, {
            replace: true,
        });
    };

    const resetMfaState = () => {
        setStep("login");
        setChallengeId(undefined);
        setMfaCode("");
        setManualEntryKey("");
        setAuthenticatorUri("");
        setRecoveryCodes([]);
        setUseRecoveryCode(false);
        setAvailableMfaMethods([authenticatorAppMethod]);
        setVerificationMfaMethods([authenticatorAppMethod]);
        setSetupMfaMethods([]);
        setSelectedMfaMethod(authenticatorAppMethod);
        setEmailDestination("");
        setEmailCodeResent(false);
        setRequestedLoginOrganizationId(undefined);
        setLoginSuccess(false);
        resetEmailCodeCooldown();
    };

    const readErrorMessage = (data: any) => {
        if (data.otherDetails === loginFailErrorCode) {
            return "Incorrect username or password. Please try again.";
        }

        return data.message || "Something went wrong. Please try again.";
    };

    const normalizeMfaMethods = (methods?: MfaMethod[]): MfaMethod[] =>
        methods?.length ? methods : [authenticatorAppMethod];

    const hasMfaMethod = (method: MfaMethod) =>
        availableMfaMethods.includes(method);
    const hasVerificationMfaMethod = (method: MfaMethod) =>
        verificationMfaMethods.includes(method);
    const hasSetupMfaMethod = (method: MfaMethod) =>
        setupMfaMethods.includes(method);

    const beginAuthenticatorMfaSetup = async (
        nextChallengeId = challengeId,
    ) => {
        if (!nextChallengeId) {
            throw new Error(
                "Your MFA setup session expired. Please sign in again.",
            );
        }

        const response = await fetch("/api/Mfa/BeginSetup", {
            method: "POST",
            headers: { "Content-Type": jsonContentType },
            body: JSON.stringify({ challengeId: nextChallengeId }),
        });

        if (!response.ok) {
            throw new Error(readErrorMessage(await response.json()));
        }

        const data: MfaSetupResponse = await response.json();
        setChallengeId(nextChallengeId);
        setManualEntryKey(data.manualEntryKey);
        setAuthenticatorUri(data.authenticatorUri);
        setStep("setup");
        setMfaCode("");
        setUseRecoveryCode(false);
    };

    const beginEmailMfaSetup = async (nextChallengeId = challengeId) => {
        if (!nextChallengeId) {
            throw new Error(
                "Your MFA setup session expired. Please sign in again.",
            );
        }

        const response = await fetch("/api/Mfa/BeginEmailSetup", {
            method: "POST",
            headers: { "Content-Type": jsonContentType },
            body: JSON.stringify({ challengeId: nextChallengeId }),
        });

        if (!response.ok) {
            throw new Error(readErrorMessage(await response.json()));
        }

        const data: MfaEmailResponse = await response.json();
        setChallengeId(nextChallengeId);
        setEmailDestination(data.maskedEmail);
        setSelectedMfaMethod(emailMethod);
        setStep("emailSetup");
        setMfaCode("");
        setUseRecoveryCode(false);
        setEmailCodeResent(false);
        startEmailCodeCooldown();
    };

    const selectEmailMfaSetup = async () => {
        if (isEmailCodeCoolingDown && emailDestination) {
            setSelectedMfaMethod(emailMethod);
            setStep("emailSetup");
            setMfaCode("");
            setUseRecoveryCode(false);
            setEmailCodeResent(false);
            return;
        }

        await beginEmailMfaSetup();
    };

    const runMfaSetupStep = async (setupStep: () => Promise<void>) => {
        setIsLoading(true);
        setErrorMessage("");

        try {
            await setupStep();
        } catch (error) {
            setErrorMessage(
                error instanceof Error
                    ? error.message
                    : "Something went wrong. Please try again.",
            );
        } finally {
            setIsLoading(false);
        }
    };

    const startMfaSetup = async (
        nextChallengeId: string,
        nextAvailableMethods?: MfaMethod[],
    ) => {
        const methods = normalizeMfaMethods(nextAvailableMethods);
        setAvailableMfaMethods(methods);
        setSetupMfaMethods(methods);
        setVerificationMfaMethods([]);
        setChallengeId(nextChallengeId);

        if (methods.length === 1 && methods[0] === emailMethod) {
            await beginEmailMfaSetup(nextChallengeId);
            return;
        }

        if (methods.length === 1) {
            await beginAuthenticatorMfaSetup(nextChallengeId);
            return;
        }

        setSelectedMfaMethod(authenticatorAppMethod);
        setStep("setupMethod");
        setMfaCode("");
        setUseRecoveryCode(false);
    };

    const getRequestedLoginOrganizationId = async () => {
        const pendingTargetOrgId = sessionStorage.getItem(
            pendingMfaTargetOrgIdStorageKey,
        );
        if (pendingTargetOrgId) {
            return pendingTargetOrgId;
        }

        const localStore = localForage.createInstance({
            name: defaultLocalStoreName,
            description: defaultLocalStoreDescription,
        });

        const selectedOrgId = await localStore.getItem<string>(
            selectedOrganizationStorageKey,
        );
        return selectedOrgId ?? undefined;
    };

    const login = async () => {
        setIsLoading(true);
        setErrorMessage("");
        setIsAccountLocked(false);

        try {
            const requestedLoginOrganizationId =
                await getRequestedLoginOrganizationId();
            const headers: Record<string, string> = {
                "Content-Type": jsonContentType,
            };

            if (requestedLoginOrganizationId) {
                headers[loginOrganizationIdHeaderName] =
                    requestedLoginOrganizationId;
            }

            const response = await fetch("/api/Authenticate/Login", {
                method: "POST",
                headers,
                body: JSON.stringify({
                    UserName: username,
                    Password: password,
                    ReturnUrl: returnURL,
                    RememberLogin: true,
                }),
            });

            const data: LoginResponse = await response.json();

            if (response.status === 200) {
                if (data.status === requiresMfaStatus) {
                    const verificationMethods = normalizeMfaMethods(
                        data.availableMethods,
                    );
                    const setupMethods = data.setupMethods ?? [];
                    const nextOrganizationId =
                        data.organizationId ?? requestedLoginOrganizationId;
                    const nextMethod = verificationMethods.includes(
                        authenticatorAppMethod,
                    )
                        ? authenticatorAppMethod
                        : verificationMethods[0];
                    const selectableMethods = Array.from(
                        new Set<MfaMethod>([
                            ...setupMethods,
                            ...verificationMethods,
                        ]),
                    );

                    setAvailableMfaMethods(selectableMethods);
                    setVerificationMfaMethods(verificationMethods);
                    setSetupMfaMethods(setupMethods);
                    setChallengeId(data.challengeId);
                    setRequestedLoginOrganizationId(nextOrganizationId);

                    if (setupMethods.length > 0 && data.challengeId) {
                        setSelectedMfaMethod(
                            setupMethods.includes(authenticatorAppMethod)
                                ? authenticatorAppMethod
                                : setupMethods[0],
                        );
                        setStep("setupMethod");
                        setMfaCode("");
                        setUseRecoveryCode(false);
                        return;
                    }

                    setSelectedMfaMethod(nextMethod);
                    if (nextMethod === emailMethod) {
                        await sendEmailMfaCode(nextOrganizationId);
                    }
                    setStep("verify");
                    setMfaCode("");
                    setUseRecoveryCode(false);
                    return;
                }

                if (data.status === requiresMfaSetupStatus) {
                    setRequestedLoginOrganizationId(
                        requestedLoginOrganizationId,
                    );
                    await startMfaSetup(
                        data.challengeId,
                        data.availableMethods,
                    );
                    return;
                }

                setLoginSuccess(true);
                await redirectToReturnUrl(requestedLoginOrganizationId);
                return;
            }

            if ((data as any).message === accountLockedMessage) {
                setIsAccountLocked(true);
                return;
            }

            setErrorMessage(readErrorMessage(data));
        } catch (error) {
            setErrorMessage("Something went wrong. Please try again.");
        } finally {
            setIsLoading(false);
        }
    };

    const sendEmailMfaCode = async (
        organizationId?: string,
        shouldShowResentMessage = false,
    ) => {
        const response = await fetch("/api/Mfa/SendEmailCode", {
            method: "POST",
            headers: { "Content-Type": jsonContentType },
            body: JSON.stringify({ organizationId }),
        });

        if (!response.ok) {
            throw new Error(readErrorMessage(await response.json()));
        }

        const data: MfaEmailResponse = await response.json();
        setEmailDestination(data.maskedEmail);
        setEmailCodeResent(shouldShowResentMessage);
        startEmailCodeCooldown();
    };

    const selectEmailVerification = async () => {
        setIsLoading(true);
        setErrorMessage("");

        try {
            if (!isEmailCodeCoolingDown) {
                await sendEmailMfaCode(requestedLoginOrganizationId);
            }
            setSelectedMfaMethod(emailMethod);
            setUseRecoveryCode(false);
            setMfaCode("");
            setStep("verify");
        } catch (error) {
            setErrorMessage(
                error instanceof Error
                    ? error.message
                    : "Something went wrong. Please try again.",
            );
        } finally {
            setIsLoading(false);
        }
    };

    const selectAuthenticatorVerification = () => {
        setSelectedMfaMethod(authenticatorAppMethod);
        setUseRecoveryCode(false);
        setMfaCode("");
        setErrorMessage("");
        setEmailCodeResent(false);
        setStep("verify");
    };

    const resendEmailCode = async () => {
        if (isEmailCodeCoolingDown || isLoading) {
            return;
        }

        setIsLoading(true);
        setErrorMessage("");

        try {
            if (step === "emailSetup") {
                await beginEmailMfaSetup();
                setEmailCodeResent(true);
                return;
            }

            await sendEmailMfaCode(requestedLoginOrganizationId, true);
            setSelectedMfaMethod(emailMethod);
            setUseRecoveryCode(false);
            setMfaCode("");
        } catch (error) {
            setErrorMessage(
                error instanceof Error
                    ? error.message
                    : "Something went wrong. Please try again.",
            );
        } finally {
            setIsLoading(false);
        }
    };

    const verifyMfa = async () => {
        setIsLoading(true);
        setErrorMessage("");

        try {
            const endpoint = useRecoveryCode
                ? "/api/Mfa/VerifyRecoveryCode"
                : selectedMfaMethod === emailMethod
                ? "/api/Mfa/VerifyEmailCode"
                : "/api/Mfa/Verify";
            const payload = useRecoveryCode
                ? {
                      recoveryCode: mfaCode,
                      returnUrl: returnURL,
                      organizationId: requestedLoginOrganizationId,
                  }
                : {
                      code: mfaCode,
                      returnUrl: returnURL,
                      organizationId: requestedLoginOrganizationId,
                  };

            const response = await fetch(endpoint, {
                method: "POST",
                headers: { "Content-Type": jsonContentType },
                body: JSON.stringify(payload),
            });

            if (!response.ok) {
                setErrorMessage(readErrorMessage(await response.json()));
                return;
            }

            setLoginSuccess(true);
            await redirectToReturnUrl(requestedLoginOrganizationId);
        } catch (error) {
            setErrorMessage("Something went wrong. Please try again.");
        } finally {
            setIsLoading(false);
        }
    };

    const completeMfaSetup = async () => {
        if (!challengeId) {
            setErrorMessage(
                "Your MFA setup session expired. Please sign in again.",
            );
            resetMfaState();
            return;
        }

        setIsLoading(true);
        setErrorMessage("");

        try {
            const response = await fetch(
                selectedMfaMethod === emailMethod
                    ? "/api/Mfa/CompleteEmailSetup"
                    : "/api/Mfa/CompleteSetup",
                {
                    method: "POST",
                    headers: { "Content-Type": jsonContentType },
                    body: JSON.stringify({
                        challengeId,
                        code: mfaCode,
                    }),
                },
            );

            if (!response.ok) {
                setErrorMessage(readErrorMessage(await response.json()));
                return;
            }

            const data: MfaSetupCompleteResponse = await response.json();
            setRecoveryCodes(
                selectedMfaMethod === emailMethod
                    ? []
                    : data.recoveryCodes || [],
            );
            setStep("setupComplete");
            setMfaCode("");
        } catch (error) {
            setErrorMessage("Something went wrong. Please try again.");
        } finally {
            setIsLoading(false);
        }
    };

    const checkForErrors = (nextUsername: string, nextPassword: string) => {
        let hasError = false;
        if (!nextUsername && !nextPassword) {
            setErrorMessage(emptyError);
            return true;
        }
        if (!nextUsername) {
            setErrorMessage(emptyUserError);
            hasError = true;
        }
        if (!nextPassword) {
            setErrorMessage(emptyPasswordError);
            hasError = true;
        }
        return hasError;
    };

    const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newPassword = e.target.value;
        setPassword(newPassword);
        if (
            (errorMessage === emptyPasswordError ||
                errorMessage === emptyError) &&
            !checkForErrors(username, newPassword)
        ) {
            setErrorMessage("");
        }
    };

    const handleUsernameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newUsername = e.target.value;
        setUsername(newUsername);
        if (
            (errorMessage === emptyUserError || errorMessage === emptyError) &&
            !checkForErrors(newUsername, password)
        ) {
            setErrorMessage("");
        }
    };

    const handleLogin = () => {
        if (!checkForErrors(username, password)) {
            login();
        }
    };

    const handleMfaSubmit = () => {
        if (!mfaCode.trim()) {
            setErrorMessage(
                useRecoveryCode
                    ? "Please enter a recovery code."
                    : selectedMfaMethod === emailMethod
                    ? "Please enter your email verification code."
                    : "Please enter your authenticator code.",
            );
            return;
        }

        if (step === "verify") {
            verifyMfa();
            return;
        }

        if (step === "setup" || step === "emailSetup") {
            completeMfaSetup();
        }
    };

    const handleKeyPress = (event: React.KeyboardEvent) => {
        if (event.key !== "Enter") {
            return;
        }

        event.preventDefault();

        if (step === "login") {
            handleLogin();
            return;
        }

        if (step === "verify" || step === "setup" || step === "emailSetup") {
            handleMfaSubmit();
        }
    };

    const setEmailCodeDigit = (index: number, value: string) => {
        const nextDigits = value.replace(/\D/g, "");
        if (!nextDigits) {
            const nextCode = mfaCode.split("");
            nextCode[index] = "";
            setMfaCode(nextCode.join(""));
            return;
        }

        if (nextDigits.length > 1) {
            const nextCode = nextDigits.slice(0, 6);
            setMfaCode(nextCode);
            emailCodeInputRefs.current[Math.min(nextCode.length, 5)]?.focus();
            return;
        }

        const nextCode = mfaCode.padEnd(6, " ").split("");
        nextCode[index] = nextDigits;
        setMfaCode(nextCode.join("").replace(/\s/g, ""));
        emailCodeInputRefs.current[index + 1]?.focus();
    };

    const handleEmailCodeKeyDown = (
        index: number,
        event: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>,
    ) => {
        if (event.key === "Backspace" && !mfaCode[index] && index > 0) {
            emailCodeInputRefs.current[index - 1]?.focus();
        }
    };

    const renderEmailCodeInput = () => (
        <Stack
            sx={{
                flexDirection: "row",
                gap: 1,
                width: "100%",
            }}
        >
            {Array.from({ length: 6 }).map((_, index) => (
                <InputBase
                    key={index}
                    inputRef={(element) => {
                        emailCodeInputRefs.current[index] = element;
                    }}
                    value={mfaCode[index] ?? ""}
                    onChange={(event) =>
                        setEmailCodeDigit(index, event.target.value)
                    }
                    onKeyDown={(event) => handleEmailCodeKeyDown(index, event)}
                    inputProps={{
                        "aria-label": `Email verification code digit ${
                            index + 1
                        }`,
                        autoComplete: "off",
                        inputMode: "numeric",
                        maxLength: 6,
                        name: `email-mfa-code-digit-${index + 1}`,
                    }}
                    sx={{
                        width: 34,
                        height: 40,
                        border: `1px solid ${theme.palette.gray[200]}`,
                        borderRadius: "4px",
                        color: theme.palette.gray[700],
                        fontSize: 16,
                        lineHeight: "24px",
                        "& input": {
                            padding: 0,
                            textAlign: "center",
                        },
                        "&.Mui-focused": {
                            borderColor: theme.palette.primary.main,
                        },
                    }}
                />
            ))}
        </Stack>
    );

    const renderEmailCodeNotice = () => (
        <MessageBox
            key={emailCodeResent ? "resent-email-code" : "sent-email-code"}
            variant="info"
            icon={<CheckCircleRoundedIcon sx={{ fontSize: 20 }} />}
            sx={{ minHeight: 40, px: 1.5, py: 1 }}
            childrenSx={{
                fontSize: "13px",
                fontWeight: 600,
                lineHeight: "20px",
            }}
        >
            Check your inbox, your code should arrive shortly.
        </MessageBox>
    );

    const renderInlineError = () =>
        errorMessage ? (
            <MessageBox
                variant="error"
                sx={{ minHeight: 40, px: 1.5, py: 1 }}
                childrenSx={{ fontSize: "14px", lineHeight: "20px" }}
            >
                {errorMessage}
            </MessageBox>
        ) : null;

    const getEmailCodeActionLabel = (label: string) =>
        isEmailCodeCoolingDown
            ? `${label} in ${emailCodeCooldownSecondsRemaining}s`
            : label;
    const isEmailCodeActionDisabled = isEmailCodeCoolingDown || isLoading;

    const emailCodeActionSx = isEmailCodeActionDisabled
        ? {
              color: theme.palette.gray[500] ?? theme.palette.text.disabled,
              pointerEvents: "none" as const,
          }
        : undefined;

    const renderTryAnotherMethod = (onClick: () => void) => (
        <Typography
            sx={{
                color: theme.palette.gray[700],
                fontSize: 14,
                lineHeight: "20px",
                textAlign: "center",
            }}
        >
            Having trouble?{" "}
            <Link component="button" type="button" onClick={onClick}>
                Try another method
            </Link>
        </Typography>
    );

    const renderSetupMethodOption = ({
        label,
        icon,
        onClick,
    }: {
        label: string;
        icon: React.ReactNode;
        onClick: () => void;
    }) => (
        <Box
            component="button"
            type="button"
            onClick={onClick}
            sx={{
                alignItems: "center",
                backgroundColor: theme.palette.white.main,
                border: `1px solid ${theme.palette.gray[200]}`,
                borderRadius: "4px",
                color: theme.palette.gray[700],
                cursor: "pointer",
                display: "flex",
                fontFamily: theme.typography.fontFamily,
                fontSize: 13,
                fontWeight: 600,
                gap: 1,
                height: 36,
                justifyContent: "space-between",
                lineHeight: "20px",
                p: "0 10px",
                textAlign: "left",
                width: "100%",
                "&:hover": {
                    borderColor: theme.palette.primary.main,
                },
            }}
        >
            <Stack
                sx={{
                    alignItems: "center",
                    flexDirection: "row",
                    gap: 1,
                    minWidth: 0,
                }}
            >
                {icon}
                <Box component="span">{label}</Box>
            </Stack>
            <ChevronRightIcon
                sx={{ color: theme.palette.gray[400], fontSize: 18 }}
            />
        </Box>
    );

    const renderAuthTitle = ({
        title,
        subtitle,
        showLogo,
        showBack,
    }: {
        title: string;
        subtitle: string;
        showLogo?: boolean;
        showBack?: boolean;
    }) => (
        <Stack
            sx={{
                gap: showLogo ? 5 : 2.5,
                mb: showLogo ? 5 : 2.5,
                width: "100%",
            }}
        >
            {showLogo && (
                <AcxLogoLoader
                    height={55}
                    width={55}
                    isSpinning={isLoading || loginSuccess}
                />
            )}
            {showBack && (
                <Box
                    component="button"
                    type="button"
                    onClick={resetMfaState}
                    sx={{
                        alignSelf: "flex-start",
                        alignItems: "center",
                        appearance: "none",
                        background: "transparent",
                        border: 0,
                        color: theme.palette.gray[500],
                        cursor: "pointer",
                        display: "inline-flex",
                        fontFamily: theme.typography.fontFamily,
                        fontSize: 12,
                        fontWeight: 600,
                        gap: 0.5,
                        lineHeight: "18px",
                        m: 0,
                        p: 0,
                        "&:hover": {
                            color: theme.palette.gray[700],
                        },
                    }}
                >
                    <KeyboardArrowLeftIcon sx={{ fontSize: 16 }} />
                    Back
                </Box>
            )}
            <Stack sx={{ gap: 0.5, width: "100%" }}>
                <Typography
                    sx={{
                        color: theme.palette.gray[600],
                        fontSize: 24,
                        fontWeight: 700,
                        lineHeight: "40px",
                    }}
                >
                    {title}
                </Typography>
                <Typography
                    sx={{
                        color: theme.palette.gray[500],
                        fontSize: 16,
                        lineHeight: "24px",
                    }}
                >
                    {subtitle}
                </Typography>
            </Stack>
        </Stack>
    );

    const getMfaTitle = () => {
        if (step === "setupComplete" && selectedMfaMethod === emailMethod) {
            return "Email MFA enabled.";
        }

        if (
            (step === "verify" &&
                selectedMfaMethod === emailMethod &&
                !useRecoveryCode) ||
            step === "emailSetup"
        ) {
            return "Check your email";
        }

        switch (step) {
            case "setupMethod":
                return "Secure your account";
            case "setup":
                return "Set up authenticator app";
            case "setupComplete":
                return "Recovery Codes";
            default:
                return "Multi-factor Authentication";
        }
    };

    const getMfaSubtitle = () => {
        if (useRecoveryCode) {
            return "Enter one of your recovery codes to finish signing in.";
        }

        if (step === "setupComplete" && selectedMfaMethod === emailMethod) {
            return "You'll receive a code to your inbox each time you log in.";
        }

        if (
            (step === "verify" && selectedMfaMethod === emailMethod) ||
            step === "emailSetup"
        ) {
            return `We sent a 6 digit code to ${
                emailDestination || "your email address"
            }.`;
        }

        switch (step) {
            case "setupMethod":
                return "Multi-factor authentication (MFA) ensures your account remains secure by verifying your identity. First choose how you'll receive your six-digit verification code on the next screen.";
            case "setup":
                return "Scan the QR code from your authenticator app. If you cannot scan it, enter the setup key manually instead.";
            case "setupComplete":
                return "Save these recovery codes somewhere safe. Each code can be used once if you lose access to your MFA method.";
            default:
                return selectedMfaMethod === emailMethod
                    ? `Enter the verification code sent to ${
                          emailDestination || "your email address"
                      } to log in.`
                    : "Enter the verification code from your authentication app to log in.";
        }
    };

    const renderMfaPage = (
        content: React.ReactNode,
        width = mfaNarrowFormWidth,
    ) => (
        <Stack sx={{ width, maxWidth: "100%" }}>
            {renderAuthTitle({
                title: getMfaTitle(),
                subtitle: getMfaSubtitle(),
                showBack: true,
            })}
            {content}
        </Stack>
    );

    const renderLoginForm = () => (
        <Stack
            component="form"
            onKeyDown={handleKeyPress}
            sx={{ width: "100%", gap: 1 }}
        >
            <AcxMainTextField
                labelText="Username"
                labelSx={{
                    fontSize: "14px",
                    mb: 1,
                    "&.Mui-focused": {
                        color: theme.palette.gray[700],
                        fontWeight: 400,
                    },
                }}
                id="login-username"
                type="text"
                fullWidth
                value={username}
                onChange={handleUsernameChange}
                placeholderText="Enter your username"
                textContainerSx={{ maxHeight: "36px" }}
            />
            <Stack
                sx={{
                    flexDirection: "row",
                    justifyContent: "space-between",
                    mt: 1.5,
                }}
            >
                <Typography component="label" htmlFor="login-password">
                    Password
                </Typography>
                <Link href="/reset" color="primary">
                    Forgot Password?
                </Link>
            </Stack>
            <AcxMainTextField
                id="login-password"
                type="password"
                fullWidth
                value={password}
                onChange={handlePasswordChange}
                placeholderText="Enter your password"
                autoComplete="off"
                sx={{ height: "36px" }}
                textContainerSx={{ maxHeight: "36px" }}
                showPasswordToggle
            />
            <AcxButton
                id="login-submit"
                fullWidth
                onClick={handleLogin}
                color="primary"
                sx={{ height: "44px", mt: 1.5 }}
                loading={isLoading || loginSuccess}
            >
                Log In
            </AcxButton>
        </Stack>
    );

    const renderVerifyForm = () => (
        <Stack
            component="form"
            onKeyDown={handleKeyPress}
            sx={{ width: "100%", gap: 1.5 }}
        >
            {selectedMfaMethod === emailMethod && !useRecoveryCode ? (
                <>
                    {renderEmailCodeInput()}
                    {emailCodeResent && renderEmailCodeNotice()}
                </>
            ) : (
                <AcxMainTextField
                    id="login-mfa-code"
                    labelText="Enter Verification Code"
                    labelSx={{
                        color: theme.palette.gray[700],
                        fontSize: "13px",
                        lineHeight: "20px",
                        mb: 1,
                        "&.Mui-focused": {
                            color: theme.palette.gray[700],
                            fontWeight: 400,
                        },
                    }}
                    type="text"
                    autoComplete="off"
                    fullWidth
                    value={mfaCode}
                    onChange={(e) => setMfaCode(e.target.value)}
                    placeholderText={
                        useRecoveryCode
                            ? "Enter your recovery code"
                            : "Enter your one-time code..."
                    }
                    textContainerSx={{ maxHeight: "36px" }}
                />
            )}
            {renderInlineError()}
            <AcxButton
                fullWidth
                onClick={handleMfaSubmit}
                color="primary"
                sx={{ height: "44px" }}
                loading={isLoading || loginSuccess}
            >
                Verify
            </AcxButton>
            <Stack sx={{ alignItems: "center", gap: 1.5, mt: 0.5 }}>
                {selectedMfaMethod === emailMethod && !useRecoveryCode && (
                    <Link
                        component="button"
                        type="button"
                        aria-disabled={isEmailCodeActionDisabled}
                        disabled={isEmailCodeActionDisabled}
                        onClick={() => void resendEmailCode()}
                        sx={emailCodeActionSx}
                    >
                        {getEmailCodeActionLabel("Resend code")}
                    </Link>
                )}
                {!useRecoveryCode &&
                    hasVerificationMfaMethod(authenticatorAppMethod) &&
                    hasVerificationMfaMethod(emailMethod) &&
                    renderTryAnotherMethod(() => {
                        if (selectedMfaMethod === emailMethod) {
                            selectAuthenticatorVerification();
                        } else {
                            void selectEmailVerification();
                        }
                    })}
                {selectedMfaMethod !== emailMethod || useRecoveryCode ? (
                    <Typography
                        sx={{
                            color: theme.palette.gray[700],
                            fontSize: 14,
                            lineHeight: "20px",
                        }}
                    >
                        {useRecoveryCode
                            ? "Have an authentication code? "
                            : "Don't have an authentication code? "}
                        <Link
                            component="button"
                            type="button"
                            onClick={() => {
                                setUseRecoveryCode((current) => !current);
                                setMfaCode("");
                                setErrorMessage("");
                            }}
                        >
                            {useRecoveryCode
                                ? "Use an authentication code"
                                : "Use a recovery code"}
                        </Link>
                    </Typography>
                ) : null}
            </Stack>
        </Stack>
    );

    const renderSetupMethod = () => (
        <Stack sx={{ width: "100%", gap: 1.5 }}>
            {hasMfaMethod(authenticatorAppMethod) && (
                <>
                    {renderSetupMethodOption({
                        label: "Authenticator app (Recommended)",
                        icon: (
                            <LockOutlinedIcon
                                sx={{
                                    color: theme.palette.gray[500],
                                    fontSize: 16,
                                }}
                            />
                        ),
                        onClick: () => {
                            if (hasSetupMfaMethod(authenticatorAppMethod)) {
                                void runMfaSetupStep(() =>
                                    beginAuthenticatorMfaSetup(),
                                );
                                return;
                            }

                            selectAuthenticatorVerification();
                        },
                    })}
                </>
            )}
            {hasMfaMethod(emailMethod) && (
                <>
                    {renderSetupMethodOption({
                        label: "Email",
                        icon: (
                            <EmailOutlinedIcon
                                sx={{
                                    color: theme.palette.gray[500],
                                    fontSize: 16,
                                }}
                            />
                        ),
                        onClick: () => {
                            if (hasSetupMfaMethod(emailMethod)) {
                                void runMfaSetupStep(selectEmailMfaSetup);
                                return;
                            }

                            void selectEmailVerification();
                        },
                    })}
                </>
            )}
        </Stack>
    );

    const renderSetupForm = () => (
        <Stack
            component="form"
            onKeyDown={handleKeyPress}
            sx={{ width: "100%", gap: 1.5 }}
        >
            <MfaSetupInstructions
                manualEntryKey={manualEntryKey}
                authenticatorUri={authenticatorUri}
            />
            <AcxMainTextField
                id="login-mfa-setup-code"
                labelText="Enter Verification Code"
                labelSx={{
                    color: theme.palette.gray[700],
                    fontSize: "13px",
                    lineHeight: "20px",
                    mb: 1,
                    "&.Mui-focused": {
                        color: theme.palette.gray[700],
                        fontWeight: 400,
                    },
                }}
                type="text"
                autoComplete="off"
                fullWidth
                value={mfaCode}
                onChange={(e) => setMfaCode(e.target.value)}
                placeholderText="Enter your one-time code..."
                textContainerSx={{ maxHeight: "36px" }}
            />
            {renderInlineError()}
            <AcxButton
                fullWidth
                onClick={handleMfaSubmit}
                color="primary"
                sx={{ height: "44px" }}
                loading={isLoading}
            >
                Verify
            </AcxButton>
            {(hasSetupMfaMethod(emailMethod) ||
                hasVerificationMfaMethod(emailMethod)) && (
                <Stack sx={{ alignItems: "center", mt: 0.5 }}>
                    {renderTryAnotherMethod(() =>
                        hasSetupMfaMethod(emailMethod)
                            ? void runMfaSetupStep(selectEmailMfaSetup)
                            : void selectEmailVerification(),
                    )}
                </Stack>
            )}
        </Stack>
    );

    const renderEmailSetupForm = () => (
        <Stack
            component="form"
            onKeyDown={handleKeyPress}
            sx={{ width: "100%", gap: 1.5 }}
        >
            {renderEmailCodeInput()}
            {emailCodeResent && renderEmailCodeNotice()}
            {renderInlineError()}
            <AcxButton
                fullWidth
                onClick={handleMfaSubmit}
                color="primary"
                sx={{ height: "44px" }}
                loading={isLoading}
            >
                Verify
            </AcxButton>
            <Stack sx={{ alignItems: "center", gap: 1.5, mt: 0.5 }}>
                <Link
                    component="button"
                    type="button"
                    aria-disabled={isEmailCodeActionDisabled}
                    disabled={isEmailCodeActionDisabled}
                    onClick={() => void resendEmailCode()}
                    sx={emailCodeActionSx}
                >
                    {getEmailCodeActionLabel("Resend code")}
                </Link>
                {(hasSetupMfaMethod(authenticatorAppMethod) ||
                    hasVerificationMfaMethod(authenticatorAppMethod)) &&
                    renderTryAnotherMethod(() =>
                        hasSetupMfaMethod(authenticatorAppMethod)
                            ? void runMfaSetupStep(() =>
                                  beginAuthenticatorMfaSetup(),
                              )
                            : selectAuthenticatorVerification(),
                    )}
            </Stack>
        </Stack>
    );

    const renderSetupComplete = () => (
        <Stack sx={{ width: "100%", gap: 1.5 }}>
            {selectedMfaMethod !== emailMethod && (
                <>
                    <MessageBox variant="success">
                        MFA is now enabled. Save these recovery codes somewhere
                        safe. Each code can be used once if you lose access to
                        your MFA method.
                    </MessageBox>
                    <Box
                        sx={{
                            border: `1px solid ${theme.palette.divider}`,
                            borderRadius: 1,
                            padding: 1.5,
                            backgroundColor: theme.palette.gray[100],
                        }}
                    >
                        {recoveryCodes.map((code) => (
                            <Typography
                                key={code}
                                sx={{
                                    fontFamily: "monospace",
                                    fontSize: "0.95rem",
                                }}
                            >
                                {code}
                            </Typography>
                        ))}
                    </Box>
                </>
            )}
            <AcxButton
                fullWidth
                onClick={() =>
                    void redirectToReturnUrl(requestedLoginOrganizationId)
                }
                color="primary"
                sx={{ height: "44px" }}
            >
                {selectedMfaMethod === emailMethod
                    ? "Finish setup"
                    : "Continue to Authenticx"}
            </AcxButton>
        </Stack>
    );

    return (
        <>
            {(step === "login" || samlLoginProvider || isAccountLocked) &&
                renderAuthTitle({
                    title: samlLoginProvider
                        ? "Welcome back!"
                        : "Welcome to Authenticx",
                    subtitle:
                        "One click away from insights that will inspire action",
                    showLogo: true,
                })}
            {!samlLoginProvider && !isAccountLocked && (
                <>
                    {step === "login" && renderLoginForm()}
                    {step === "verify" &&
                        renderMfaPage(
                            renderVerifyForm(),
                            selectedMfaMethod === emailMethod &&
                                !useRecoveryCode
                                ? mfaWideFormWidth
                                : mfaNarrowFormWidth,
                        )}
                    {step === "setupMethod" &&
                        renderMfaPage(renderSetupMethod(), mfaWideFormWidth)}
                    {step === "setup" &&
                        renderMfaPage(renderSetupForm(), mfaWideFormWidth)}
                    {step === "emailSetup" &&
                        renderMfaPage(renderEmailSetupForm(), mfaWideFormWidth)}
                    {step === "setupComplete" &&
                        renderMfaPage(
                            renderSetupComplete(),
                            selectedMfaMethod === emailMethod
                                ? mfaNarrowFormWidth
                                : mfaWideFormWidth,
                        )}
                    {errorMessage &&
                        !["verify", "setup", "emailSetup"].includes(step) && (
                            <MessageBox sx={{ mt: 1.5 }} variant="error">
                                {errorMessage}
                            </MessageBox>
                        )}
                </>
            )}
            {isAccountLocked && (
                <MessageBox>
                    Your account has been locked. Account reset information has
                    been sent to the email on file.
                </MessageBox>
            )}
            {samlLoginProvider && (
                <Stack spacing={1.5} sx={{ width: "100%" }}>
                    {(samlLoginProvider.providers?.length
                        ? samlLoginProvider.providers
                        : [
                              {
                                  providerKey: undefined,
                                  loginButtonText:
                                      samlLoginProvider.loginButtonText,
                              },
                          ]
                    ).map((provider) => (
                        <AcxButton
                            key={provider.providerKey ?? "default"}
                            fullWidth
                            sx={{
                                marginInline: 0,
                                height: "36px",
                            }}
                            color="primary"
                            loading={isLoading}
                            href={getSamlUrl(provider.providerKey)}
                        >
                            {provider.loginButtonText ??
                                "Authorize with SAML Provider"}
                        </AcxButton>
                    ))}
                </Stack>
            )}
        </>
    );
}
